import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readEventStream, reduceTurnEvent } from "./sse";
import { parseArtifactMessage } from "./artifacts";
import { THINKING_LEVELS, thinkingState, readThinking, saveThinking } from "./thinking";
import { ChatImageError, MAX_IMAGES, MAX_IMAGE_BYTES } from "@/lib/chatImages.mjs";

import { ChatDocumentError, DOCUMENT_ERRORS, documentMime, validateChatAttachments } from "@/lib/chatDocuments.mjs";
const attachmentPayload = (row) => ({
  images: (row.draftImages || []).map(({ name, dataUrl }) => ({ name, dataUrl })),
  documents: (row.draftDocuments || []).map(({ name, mimeType, base64 }) => ({ name, mimeType, base64 })),
});
const validateDraft = (row) => { const { images, documents } = attachmentPayload(row); return validateChatAttachments(images, documents); };

const initialState = (scopeKey) => ({
  scopeKey, agent: { name: "Your agent", available: false, online: false, stop: false },
  thinking: { ...thinkingState(), status: "loading" },
  sessions: [], activeId: null, selectedArtifactId: null, loading: true, error: null,
});
const sessionRow = (row, scopeKey) => ({
  id: row.id, title: row.title || "New session", messages: [], draft: "", status: "idle",
  draftImages: [], draftDocuments: [], documentsExtracting: false, imagesLoading: false, imageError: null,
  live: null, error: null, archived: false, pinned: false, updatedAt: row.updatedAt || null,
  messageCount: row.messageCount || 0, historyLoaded: false, historyLoading: false,
  reasoningEffort: readThinking(scopeKey, row.id), thinkingError: null, stopping: false,
});
const errorMessage = (cause) => cause?.code === "agent_unreachable"
  ? "Your agent is offline. Refresh to reconnect when it is back."
  : cause?.code === "workspace_changed" ? "The workspace changed. Open this session in its original workspace."
    : "The request could not be completed. Refresh the session before trying again.";
const STOPPED_EMPTY = "(Stopped before any text)";
const STOP_UNAVAILABLE = "Stop isn't available for this agent yet. The answer will finish on its own.";
const STOP_UNCONFIRMED = "Stop was sent but the agent has not confirmed it. Refresh to recover the latest answer.";
// Any other failed stop. The answer is still streaming, so neither "offline" nor "refresh" (which is off during a run) fits.
const STOP_FAILED = "Stop didn't go through. Try again, or let the answer finish.";
const deferred = () => { let resolve; let reject; const promise = new Promise((a, b) => { resolve = a; reject = b; }); return { promise, resolve, reject }; };

/** Completed deliverables only; repeated links must not flood a session's file list. */
export function responseArtifacts(sessions) {
  return sessions.filter((session) => !session.archived).flatMap((session) => {
    const seenUrls = new Set();
    return (session.messages || []).flatMap((message, messageIndex) => {
      if (message.role !== "agent" || message.incomplete) return [];
      return parseArtifactMessage(message.text).artifacts.flatMap(({ index, ...artifact }) => {
        if (artifact.url && seenUrls.has(artifact.url)) return [];
        if (artifact.url) seenUrls.add(artifact.url);
        return [{ ...artifact, id: `${session.id}:message:${messageIndex}:artifact:${index}`, sessionId: session.id }];
      });
    });
  });
}

/**
 * Mount once in the shell. Every stream belongs to its own session, inside one identity/workspace.
 * stopTimeoutMs is how long a stop the agent has accepted may take to end the stream before this tab gives
 * up on it. The default is the real limit; the option exists so tests can shorten it.
 */
export function useChatWorkspace({ api, scopeKey, agentName, stopTimeoutMs = 10000 }) {
  const [state, setState] = useState(() => initialState(scopeKey));
  const stateRef = useRef(state);
  const scopeRef = useRef(scopeKey);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);
  const streamsRef = useRef(new Map());
  // One per running turn, by session id: the way out if a stop is accepted but the stream never ends.
  const stopWaitsRef = useRef(new Map());
  const historyRef = useRef(new Map());
  const imageReadsRef = useRef(new Map());
  const imageSequenceRef = useRef(0);
  const mutationRef = useRef(new Set());
  const refreshRef = useRef(0);
  const thinkingRequestRef = useRef(0);
  const turnSequenceRef = useRef(0);
  // Immediately hide previous identity data, before effects or pending requests settle.
  scopeRef.current = scopeKey;
  const current = useCallback((generation) => mountedRef.current
    && scopeRef.current === scopeKey && generationRef.current === generation, [scopeKey]);
  const update = useCallback((change, generation = generationRef.current) => {
    if (!current(generation) || stateRef.current.scopeKey !== scopeKey) return;
    const next = change(stateRef.current);
    stateRef.current = next;
    setState(next);
  }, [current, scopeKey]);
  const updateSession = useCallback((id, change, generation) => update((previous) => ({
    ...previous, sessions: previous.sessions.map((row) => row.id === id ? change(row) : row),
  }), generation), [update]);

  const loadHistory = useCallback(async (id, force = false) => {
    const generation = generationRef.current;
    const session = stateRef.current.sessions.find((row) => row.id === id);
    if (!session || !current(generation) || streamsRef.current.has(id) || historyRef.current.has(id)
      || (!force && session.historyLoaded)) return;
    const request = {};
    historyRef.current.set(id, request);
    updateSession(id, (row) => ({ ...row, historyLoading: true, error: null }), generation);
    try {
      const payload = await api.getAgentChatSession(id);
      if (!current(generation) || historyRef.current.get(id) !== request) return;
      if (payload?.sessionId && payload.sessionId !== id) throw new Error("session_mismatch");
      updateSession(id, (row) => ({
        ...row, messages: Array.isArray(payload?.messages) ? payload.messages : [],
        historyLoaded: true, historyLoading: false, status: "idle", error: null, requiresRefresh: false,
      }), generation);
    } catch (cause) {
      updateSession(id, (row) => ({ ...row, historyLoading: false, error: errorMessage(cause) }), generation);
    } finally {
      if (historyRef.current.get(id) === request) historyRef.current.delete(id);
    }
  }, [api, current, updateSession]);

  const refresh = useCallback(async () => {
    const generation = generationRef.current;
    const request = ++refreshRef.current;
    const thinkingRequest = ++thinkingRequestRef.current;
    update((previous) => ({ ...previous, loading: true, error: null }), generation);
    try {
      const payload = await api.getAgentChat();
      if (!current(generation) || refreshRef.current !== request) return;
      update((previous) => {
        const existing = new Map(previous.sessions.map((row) => [row.id, row]));
        const rows = (payload?.sessions || []).map((row) => {
          const local = existing.get(row.id);
          existing.delete(row.id);
          return local ? { ...local, title: local.status === "running" ? local.title : row.title || local.title, updatedAt: row.updatedAt || local.updatedAt }
            : sessionRow(row, scopeKey);
        });
        // Newly created/in-flight and locally archived chats remain usable if list indexing lags.
        const sessions = [...rows, ...existing.values()];
        return {
          ...previous, sessions, loading: false, error: null,
          thinking: thinkingRequestRef.current === thinkingRequest ? thinkingState(payload?.thinking) : previous.thinking,
          activeId: previous.activeId || sessions.find((row) => !row.archived)?.id || null,
          agent: { name: payload?.agentName || "Your agent", online: Boolean(payload?.online), available: Boolean(payload?.available), stop: Boolean(payload?.stop) },
        };
      }, generation);
      const id = stateRef.current.activeId;
      if (id) await loadHistory(id, true);
    } catch (cause) {
      if (refreshRef.current !== request) return;
      update((previous) => ({ ...previous, loading: false, error: errorMessage(cause),
        thinking: thinkingRequestRef.current === thinkingRequest ? thinkingState() : previous.thinking,
      }), generation);
    }
  }, [api, current, loadHistory, update, scopeKey]);

  const refreshThinking = useCallback(async () => {
    if (!api.getAgentChatThinking) return;
    const generation = generationRef.current;
    const request = ++thinkingRequestRef.current;
    try {
      const payload = await api.getAgentChatThinking();
      if (!current(generation) || thinkingRequestRef.current !== request) return;
      update((previous) => ({ ...previous, thinking: thinkingState(payload?.thinking) }), generation);
    } catch {
      if (!current(generation) || thinkingRequestRef.current !== request) return;
      update((previous) => ({ ...previous, thinking: thinkingState() }), generation);
    }
  }, [api, current, update]);

  useEffect(() => {
    const streams = streamsRef.current;
    const stopWaits = stopWaitsRef.current;
    const histories = historyRef.current;
    const imageReads = imageReadsRef.current;
    const mutations = mutationRef.current;
    mountedRef.current = true;
    generationRef.current += 1;
    stateRef.current = initialState(scopeKey);
    setState(stateRef.current);
    refresh();
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      // Detach readers on identity changes. The gateway still completes agent work remotely.
      streams.forEach((controller) => controller.abort());
      streams.clear();
      stopWaits.forEach((wait) => clearTimeout(wait.timer));
      stopWaits.clear();
      histories.clear();
      imageReads.forEach((request) => request.readers.forEach((reader) => reader.abort()));
      imageReads.clear();
      mutations.clear();
    };
  }, [scopeKey, refresh]);

  useEffect(() => {
    const check = () => { if (document.visibilityState !== "hidden") refreshThinking(); };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    const timer = setInterval(check, 60_000);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [refreshThinking]);

  const setActiveId = useCallback((id) => {
    if (!stateRef.current.sessions.some((row) => row.id === id && !row.archived)) return;
    update((previous) => ({ ...previous, activeId: id, selectedArtifactId: null }));
    loadHistory(id);
  }, [loadHistory, update]);

  const createSession = useCallback(async (options) => {
    const generation = generationRef.current;
    if (!current(generation) || !stateRef.current.agent.available) return null;
    // React also passes a click event when this callback is used directly as onClick.
    // Only an explicit own draft field opts into seeded creation; capture it before awaiting.
    const hasDraft = options !== null && typeof options === "object" && Object.hasOwn(options, "draft");
    const initialDraft = hasDraft ? options.draft : "";
    // Task dialogs can finish creation after being dismissed. Let their caller validate
    // the handoff before selecting, without a late response taking over the active chat.
    const activate = !(options !== null && typeof options === "object" && Object.hasOwn(options, "activate") && options.activate === false);
    const invalidOptions = options != null && (typeof options !== "object" || Array.isArray(options));
    if (invalidOptions || (hasDraft && (typeof initialDraft !== "string" || !initialDraft.trim() || initialDraft.length > 8000))) {
      update((previous) => ({ ...previous, error: "Start with a non-empty draft of at most 8,000 characters." }), generation);
      return null;
    }
    update((previous) => ({ ...previous, error: null }), generation);
    try {
      const payload = await api.createAgentChatSession();
      if (!current(generation)) return null;
      if (!payload?.session?.id) throw new Error("session_missing");
      const row = { ...sessionRow(payload.session, scopeKey), draft: initialDraft, historyLoaded: true };
      update((previous) => ({
        ...previous, sessions: [row, ...previous.sessions],
        activeId: activate ? row.id : previous.activeId,
        selectedArtifactId: activate ? null : previous.selectedArtifactId,
      }), generation);
      return row.id;
    } catch (cause) {
      update((previous) => ({ ...previous, error: errorMessage(cause) }), generation);
      return null;
    }
  }, [api, current, update, scopeKey]);

  const updateDraft = useCallback((id, draft) => updateSession(id, (row) => ({ ...row, draft })), [updateSession]);
  const addImages = useCallback(async (id, files) => {
    const generation = generationRef.current;
    const row = stateRef.current.sessions.find((session) => session.id === id);
    if (!current(generation) || !row || row.archived || imageReadsRef.current.has(id) || !files?.length) return;
    const request = { readers: [] };
    imageReadsRef.current.set(id, request);
    const valid = () => current(generation) && imageReadsRef.current.get(id) === request
      && stateRef.current.sessions.some((session) => session.id === id && !session.archived);
    updateSession(id, (session) => ({ ...session, imagesLoading: true, imageError: null }), generation);
    try {
      const selected = Array.from(files);
      const allImages = !row.draftDocuments.length && selected.every((file) => !documentMime(file.name));
      if (selected.length + row.draftImages.length + row.draftDocuments.length > MAX_IMAGES) {
        throw allImages ? new ChatImageError('chat_images_too_many') : new ChatDocumentError('chat_attachments_too_many');
      }
      for (const file of selected) {
        const mime = documentMime(file.name);
        if (mime) {
          if (file.type && file.type !== 'application/octet-stream' && file.type !== mime) throw new ChatDocumentError();
        } else if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new ChatDocumentError();
      }
      if (selected.reduce((size, file) => size + file.size, 0) > MAX_IMAGE_BYTES) {
        throw allImages ? new ChatImageError('chat_images_too_large', 413) : new ChatDocumentError('chat_attachments_too_large', 413);
      }
      const attachments = await Promise.all(selected.map((file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        request.readers.push(reader);
        reader.onload = () => {
          const common = { id: `attachment-${++imageSequenceRef.current}`, name: file.name };
          const mimeType = documentMime(file.name);
          resolve(mimeType ? { ...common, mimeType, base64: String(reader.result).split(',')[1] } : { ...common, dataUrl: reader.result });
        };
        reader.onerror = reader.onabort = () => reject(new Error('attachment_read_failed'));
        reader.readAsDataURL(file);
      })));
      if (!valid()) return;
      const images = attachments.filter((a) => !a.mimeType), documents = attachments.filter((a) => a.mimeType);
      validateDraft({ draftImages: images, draftDocuments: documents });
      // Re-read after async reads: removal, restoration and newer drafts still belong to this generation.
      const latest = stateRef.current.sessions.find((session) => session.id === id);
      const combined = { draftImages: [...latest.draftImages, ...images], draftDocuments: [...latest.draftDocuments, ...documents] };
      try { validateDraft(combined); }
      catch (cause) {
        const restored = [...latest.draftImages, ...latest.draftDocuments].some((a) => ![...row.draftImages, ...row.draftDocuments].some((prior) => prior.id === a.id));
        if (['chat_images_too_many', 'chat_images_too_large', 'chat_attachments_too_many', 'chat_attachments_too_large'].includes(cause.code) && restored) {
          updateSession(id, (session) => ({ ...session, ...combined, imageError: cause.message }), generation);
          return;
        }
        throw cause;
      }
      updateSession(id, (session) => ({ ...session, ...combined, imageError: null }), generation);
    } catch (cause) {
      if (valid()) updateSession(id, (session) => ({ ...session, imageError: cause instanceof ChatImageError || cause instanceof ChatDocumentError ? cause.message : "Couldn't read that attachment. Select it again." }), generation);
    } finally {
      if (valid()) updateSession(id, (session) => ({ ...session, imagesLoading: false }), generation);
      if (imageReadsRef.current.get(id) === request) imageReadsRef.current.delete(id);
    }
  }, [current, updateSession]);
  const removeImage = useCallback((id, imageId) => updateSession(id, (row) => ({
    ...row, draftImages: row.draftImages.filter((image) => image.id !== imageId), imageError: null,
  })), [updateSession]);
  const removeDocument = useCallback((id, documentId) => updateSession(id, (row) => ({
    ...row, draftDocuments: row.draftDocuments.filter((doc) => doc.id !== documentId), imageError: null,
  })), [updateSession]);
  const setReasoningEffort = useCallback((id, effort) => {
    const generation = generationRef.current;
    const row = stateRef.current.sessions.find((session) => session.id === id);
    if (!current(generation) || !row || row.archived || stateRef.current.thinking.status !== "enabled"
      || (effort !== null && !THINKING_LEVELS.includes(effort))) return false;
    const saved = saveThinking(scopeKey, id, effort);
    updateSession(id, (session) => ({ ...session, reasoningEffort: effort,
      thinkingError: saved ? null : "Applied for this visit. Your browser couldn't remember this setting.",
    }), generation);
    return true;
  }, [current, scopeKey, updateSession]);
  const send = useCallback(async (id) => {
    const generation = generationRef.current;
    const row = stateRef.current.sessions.find((session) => session.id === id);
    const message = row?.draft.trim();
    const draftImages = row?.draftImages || [];
    const draftDocuments = row?.draftDocuments || [];
    if (!current(generation) || !row || (!message && !draftImages.length && !draftDocuments.length) || row.imagesLoading || imageReadsRef.current.has(id) || row.archived || row.requiresNewSession || row.requiresRefresh || row.historyLoading || !row.historyLoaded
      || streamsRef.current.has(id) || !stateRef.current.agent.available) return;
    if (message.length > 8000) {
      updateSession(id, (session) => ({ ...session, error: "Keep each message under 8,000 characters." }), generation);
      return;
    }
    let images, documents;
    try { ({ images, documents } = validateDraft(row)); }
    catch (cause) {
      updateSession(id, (session) => ({ ...session, imageError: cause.message }), generation);
      return;
    }
    const controller = new AbortController();
    streamsRef.current.set(id, controller);
    const clientTurnId = `${id}:${++turnSequenceRef.current}`;
    // stop() starts the timer once the agent has accepted a stop. If the stream then never ends, the timer rejects this wait.
    const wait = { ...deferred(), timer: null };
    // The timer can run out while the response is still pending, before the race below has a listener on the wait.
    wait.promise.catch(() => {});
    stopWaitsRef.current.set(id, wait);
    let dispatched = false;
    let turn = { text: "", tool: "", status: "running", model: "", runId: null, interrupted: false };
    const stoppedMessage = () => ({ role: "agent", text: turn.text || STOPPED_EMPTY, stopped: true, at: new Date().toISOString() });
    updateSession(id, (session) => ({
      ...session, draft: "", draftImages: [], draftDocuments: [], documentsExtracting: documents.length > 0, imageError: null, status: "running", error: null, live: turn,
      messages: [...session.messages, { role: "user", text: message, ...(images.length ? { images: draftImages } : {}), ...(documents.length ? { documents: documents.map(({ name, mimeType }) => ({ name, mimeType })) } : {}), at: new Date().toISOString(), clientTurnId }],
    }), generation);
    try {
      // Capture the choice with this turn. Another session or a mid-run change cannot alter it.
      const reasoning = stateRef.current.thinking.status === "enabled" && THINKING_LEVELS.includes(row.reasoningEffort)
        ? { reasoningEffort: row.reasoningEffort } : {};
      const response = await api.streamAgentChatTurn({ sessionId: id, message, ...(images.length ? { images } : {}), ...(documents.length ? { documents } : {}), ...reasoning, signal: controller.signal });
      if (!current(generation)) return;
      dispatched = true;
      updateSession(id, (session) => ({ ...session, documentsExtracting: false }), generation);
      const reading = readEventStream(response, (name, payload) => {
        if (!current(generation)) return;
        turn = reduceTurnEvent(turn, name, payload);
        updateSession(id, (session) => ({ ...session, live: { ...turn } }), generation);
      });
      // The stream ends the turn. A stop the agent accepted but never confirms rejects the wait instead.
      await Promise.race([reading, wait.promise]);
      if (!current(generation)) return;
      if (turn.status !== "done") throw new Error("run_incomplete");
      if (turn.interrupted) {
        // The agent cut the turn short, normally because of a stop. What it wrote stays, labelled, and the draft is untouched.
        updateSession(id, (session) => ({
          ...session, status: "idle", live: null, error: null, stopping: false, updatedAt: new Date().toISOString(),
          messages: [...session.messages, stoppedMessage()],
        }), generation);
        return;
      }
      if (turn.model === "alloy-out-of-credits") {
        updateSession(id, (session) => ({ ...session, status: "error", live: null, stopping: false, error: "Your agent could not reach its model. Start a new session after checking your Alloy tokens.", requiresNewSession: true }), generation);
        return;
      }
      updateSession(id, (session) => ({
        ...session, status: "idle", live: null, error: null, stopping: false, updatedAt: new Date().toISOString(),
        messages: [...session.messages, { role: "agent", text: turn.text || "(No text response)", at: new Date().toISOString() }],
      }), generation);
    } catch (cause) {
      // Once a stop has been asked for, the stream ending any way at all answers it; that is not a failed send. Before
      // the stream opened there is nothing to stop, so a refusal at that point still takes the usual path below.
      const stopping = dispatched && current(generation) && stateRef.current.sessions.some((session) => session.id === id && session.stopping);
      if (stopping) {
        // The wait ran out: stop reading, keep what was written, and ask for a refresh. Any other ending (an error or
        // run.failed event, a reader that threw) counts as the stop taking effect, so there is nothing to recover.
        const unconfirmed = cause?.message === "stop_unconfirmed";
        if (unconfirmed) controller.abort();
        updateSession(id, (session) => ({
          ...session, live: null, stopping: false, messages: [...session.messages, stoppedMessage()],
          ...(unconfirmed ? { status: "error", requiresRefresh: true, error: STOP_UNCONFIRMED }
            : { status: "idle", requiresRefresh: false, error: null, updatedAt: new Date().toISOString() }),
        }), generation);
        return;
      }
      // Only a definite refusal can safely return the prompt to the composer. A dropped
      // connection may already have started work, so refresh history before another turn.
      const thinkingRefused = ["chat_thinking_requires_alloy", "chat_thinking_unavailable", "chat_reasoning_effort_invalid"].includes(cause?.code);
      if (thinkingRefused && current(generation)) {
        // Invalidate an older capability refresh that began before this live refusal.
        thinkingRequestRef.current += 1;
        update((previous) => ({ ...previous, thinking: { ...previous.thinking,
          status: cause.code === "chat_thinking_requires_alloy" ? "off" : "unavailable",
        } }), generation);
      }
      const documentRefused = ["chat_document_unreadable", "chat_document_conversion_failed", "chat_document_text_too_large", "chat_document_invalid"].includes(cause?.code);
      const refused = !dispatched && (thinkingRefused || documentRefused || [400, 401, 403, 413].includes(cause?.status)
        || ["client_auth_missing", "workspace_changed"].includes(cause?.code));
      updateSession(id, (session) => ({
        ...session, status: "error", live: null, documentsExtracting: false, stopping: false, requiresRefresh: !refused,
        draft: refused ? (!session.draft || session.draft === row.draft ? row.draft : !row.draft ? session.draft : `${row.draft}\n\n${session.draft}`) : session.draft,
        draftImages: refused ? [...draftImages, ...session.draftImages] : session.draftImages,
        draftDocuments: refused ? [...draftDocuments, ...session.draftDocuments] : session.draftDocuments,
        imageError: refused && DOCUMENT_ERRORS[cause?.code] ? DOCUMENT_ERRORS[cause.code] : refused && cause?.code?.startsWith('chat_image') ? new ChatImageError(cause.code).message : session.imageError,
        error: thinkingRefused ? "Thinking controls are unavailable. Your message wasn't sent and your draft is saved. Refresh to check Alloy, or send again with your agent's default."
          : refused ? "The message was not accepted. Your draft is saved; check your connection or sign-in and try again."
          : cause?.message === "run_incomplete" ? "The connection ended before completion. Refresh to recover the latest answer." : errorMessage(cause),
        messages: refused ? session.messages.filter((item) => item.clientTurnId !== clientTurnId)
          : turn.text ? [...session.messages, { role: "agent", text: turn.text, incomplete: true }] : session.messages,
      }), generation);
    } finally {
      if (stopWaitsRef.current.get(id) === wait) { clearTimeout(wait.timer); stopWaitsRef.current.delete(id); }
      if (streamsRef.current.get(id) === controller) streamsRef.current.delete(id);
    }
  }, [api, current, update, updateSession]);

  /**
   * Ask the agent to stop the turn this tab is streaming for session id. True once the agent has taken the stop; false
   * when it is refused here (the agent has no stop, this tab has no stream for the session, the stream has not named its
   * run yet, a stop is already on its way) or the request fails. Nothing is finished here: the stream ending, or the wait
   * running out, ends the turn in send.
   */
  const stop = useCallback(async (id) => {
    const generation = generationRef.current;
    const row = stateRef.current.sessions.find((session) => session.id === id);
    const wait = stopWaitsRef.current.get(id);
    // The run id comes with the stream's first event, and the button waits for it too. Before it nothing is sent: a stop
    // the agent answered as idle would start the timer below and abandon a turn that is running normally.
    if (!current(generation) || !row || !wait || row.stopping || !stateRef.current.agent.stop || !streamsRef.current.has(id) || !row.live?.runId) return false;
    // A stop that goes out clears what an earlier failed one said.
    updateSession(id, (session) => ({ ...session, stopping: true, error: null }), generation);
    try {
      await api.stopAgentChatTurn({ sessionId: id, runId: row.live.runId });
    } catch (cause) {
      // A turn that ended while the stop was on its way has nothing left to stop and nothing to report.
      if (stopWaitsRef.current.get(id) === wait) {
        updateSession(id, (session) => ({ ...session, stopping: false,
          error: cause?.code === "chat_stop_unavailable" ? STOP_UNAVAILABLE : STOP_FAILED,
        }), generation);
      }
      return false;
    }
    // The agent took the stop, so the stream should end within a second or two. If it does not, give up on it.
    if (stopWaitsRef.current.get(id) === wait) wait.timer = setTimeout(() => wait.reject(new Error("stop_unconfirmed")), stopTimeoutMs);
    return true;
  }, [api, current, stopTimeoutMs, updateSession]);

  const renameSession = useCallback(async (id, title) => {
    const generation = generationRef.current;
    const clean = String(title || "").trim().slice(0, 120);
    if (!clean || !current(generation) || mutationRef.current.has(id)) return false;
    mutationRef.current.add(id);
    try {
      await api.renameAgentChatSession(id, clean);
      updateSession(id, (row) => ({ ...row, title: clean, error: null }), generation);
      return current(generation);
    } catch (cause) {
      updateSession(id, (row) => ({ ...row, error: errorMessage(cause) }), generation);
      return false;
    } finally { mutationRef.current.delete(id); }
  }, [api, current, updateSession]);
  const togglePin = useCallback((id) => updateSession(id, (row) => ({ ...row, pinned: !row.pinned })), [updateSession]);
  const archiveSession = useCallback(async (id) => {
    const generation = generationRef.current;
    if (!current(generation) || streamsRef.current.has(id) || mutationRef.current.has(id)) return false;
    mutationRef.current.add(id);
    try {
      await api.archiveAgentChatSession(id);
      update((previous) => ({
        ...previous, sessions: previous.sessions.map((row) => row.id === id ? { ...row, archived: true } : row),
        activeId: previous.activeId === id ? previous.sessions.find((row) => row.id !== id && !row.archived)?.id || null : previous.activeId,
      }), generation);
      const next = stateRef.current.activeId;
      if (current(generation) && next) loadHistory(next);
      return current(generation);
    } catch (cause) {
      updateSession(id, (row) => ({ ...row, error: errorMessage(cause) }), generation);
      return false;
    } finally { mutationRef.current.delete(id); }
  }, [api, current, loadHistory, update, updateSession]);
  const setSelectedArtifactId = useCallback((id) => update((previous) => ({ ...previous, selectedArtifactId: id })), [update]);
  const visible = state.scopeKey === scopeKey ? state : initialState(scopeKey);
  const artifacts = useMemo(() => responseArtifacts(visible.sessions), [visible.sessions]);
  return {
    ...visible, agent: { ...visible.agent, name: agentName || visible.agent.name }, artifacts,
    setActiveId, createSession, updateDraft, addImages, removeImage, removeDocument, setReasoningEffort, send, stop, renameSession, togglePin, archiveSession,
    setSelectedArtifactId, refresh, refreshThinking, ensureSessionLoaded: loadHistory, loadSession: loadHistory,
  };
}

export default useChatWorkspace;
